import Axios from 'axios';
import {
  clearString,
  getSlugFromIDs,
  notification,
  pageHasContent,
  TokenExpiredNotification,
} from '../assets/constants/strings';
import config from '../config';
import store from '../store';
import {
  CONFIRM_EMAIL,
  CREATE_BOOKMARK,
  DELETE_BOOKMARK,
  DELETE_BOOKMARKS,
  GET_BOOKMARKS,
  LOG_IN,
  LOG_OUT,
  REGISTER,
  RESEND_EMAIL,
  RESET_PASS,
  UPDATE_PASS,
  UPDATE_USER,
  INVITE_USER,
  INVITE_USER_ERROR,
  GET_USERS_BY_CLIENT_ID,
  DELETE_USER_CLIENT,
  UPDATE_CLIENT_ROLE,
  REINVITE_USER,
  REINVITE_USER_ERROR,
  SEND_ENQUIRY,
  SUBSCRIPTION_UPDATE,
  CREATE_CLIENT_SUBSCRIPTION,
  GET_USER_PLANS,
  UPDATE_USER_USAGE,
  GET_USER_USAGE,
} from '../store/User/userTypes';
import {
  API_BASE,
  API_CLIENT,
  API_DOCUMENTS,
  // API_DOCUMENT_BY_ID,
  API_GLOSSARY,
  API_GLOSSARY_RELATIONSHIP,
  API_LEVEL_FOUR,
  API_LEVEL_ONE,
  API_LEVEL_THREE,
  API_LEVEL_TWO,
  API_LOGO,
  API_NAVIGATION,
  API_PAGES_BY_DOC,
  API_RELATED_CONTENT,
  API_RELATED_CONTENT_RELATIONSHIP,
  API_RELATED_DOCUMENT,
  API_RELATED_SECTION,
  API_PAGES,
  AUTHENTICATE,
  CONFIRM_EMAIL as CONFIRM_EMAIL_ENDPOINT,
  CREATE_BOOKMARK_API,
  DELETE_BOOKMARKS_API,
  DELETE_BOOKMARK_API,
  FORGOT_PASSWORD,
  GET_USER_BOOKMARKS,
  INTERNAL_ERROR,
  REGISTER as REGISTER_API,
  RESEND_EMAIL_API,
  RESET_PASSWORD,
  UPDATE_USER_API,
  CHANGE_PASSWORD_API,
  REFRESH_TOKEN_API,
  ACTIVATE_PRODUCT_API,
  LOG_OUT_API,
  CANCEL_REASON_API,
  API_SEARCH,
  CREATE_COMMENT_API,
  API_GET_LIST_BY_ID,
  API_DOCUMENT_BY_SLUG,
  API_DOCUMENT_BY_ID,
  API_DOCUMENT_BY_NEW_VERSION_ID,
  // GET_CLIENT_PRODUCT_BY_ID_API,
  INVITE_USER_API,
  GET_USERS_BY_CLIENT_ID_API,
  DELETE_USER_CLIENT_API,
  UPDATE_CLIENT_ROLE_API,
  REINVITE_USER_API,
  SEND_ENQUIRY_API,
  API_GET_DOC_SUMMARY,
  UPDATE_CLIENT_API,
  CREATE_CLIENT_SUBSCRIPTION_API,
  UPDATE_CLIENT_SUBSCRIPTION_API,
  GET_PLANS,
  USAGE_BY_USER_ID_API,
  UPDATE_USER_USAGE_API,
  API_USAGE_MY,
  API_KEYS_MY,
  CREATE_API_KEY,
  REVOKE_API_KEY,
  GET_QUERY_BY_USER_ID_API,
  GET_QUERY_MESSAGES_BY_QUERY_ID_API,
  CREATE_NEW_QUERY_API,
  UPDATE_QUERY_API,
  DELETE_QUERY_API,
  CREATE_NEW_MESSAGE_API,
  UPDATE_MESSAGES_RESPONSE_API,
  BUY_ADDITIONAL_QUERIES_API,
} from './consts';
import { GET_MESSAGES, GET_QUERIES } from '../store/Revealr/revealrTypes';
import { BUY_ADDITIONAL_QUERIES } from '../store/Client/clientTypes';

const BASE_URL = config.baseURL;
const AUTH_BASE = config.authBaseURL;
const IMAGES_FOLDER = config.ImagesFolder;

/**
 * Base json type header for all Axios calls.
 */
const HEADER = {
  'Content-Type': 'application/json',
};

/**
 * Defensively extract a human-readable reason from an axios error.
 *
 * MUST be null-safe: on a network-level failure (timeout / abort / CORS / server
 * unreachable) axios provides NO `e.response`, so reading `e.response.data`
 * directly — as the old inline ternaries did — throws a TypeError *inside* the
 * catch block, rejecting the promise. In the login/refresh path that silently
 * drops the user's session/entitlements (e.g. it left the /api "Activate" flow
 * stuck on "Activating…" after a transient blip during an API cold-start).
 *
 * Preserves the original success-path behaviour: prefer the API's `Message`,
 * else fall back to the raw response body, else the JS error message.
 */
const getErrReason = (e) => {
  const resp = e && e.response;
  if (resp && resp.data) return resp.data.Message || resp.data;
  return (e && e.message) || INTERNAL_ERROR;
};

/**
 * Reader content header.
 *
 * Content now flows through the .NET content gateway (api/v1/Content), authenticated with the
 * logged-in user's JWT. The WordPress spKey is injected server-side by the gateway and is no
 * longer sent from the browser.
 */
const SP_HEADER = () => {
  const token =
    store.getState().user &&
    store.getState().user.user &&
    store.getState().user.user.token;
  return {
    ...HEADER,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Swagger.API calls auth header
 * @param {string} token
 * @returns Header with token.
 */
const AUTH_HEADER = (token) => ({
  ...HEADER,
  Authorization: `Bearer ${token}`,
});

const axios = Axios.create({
  BASE_URL,
  withCredentials: true,
});

const loadText = (text) => {
  if (document.getElementById('extra-load-text'))
    document.getElementById('extra-load-text').innerText = text;
};

//#region Client Reducer

/**
 * Fetches a level list
 * @param {string} level The list to be fetched (levels one through four)
 * @returns The level list according to the level param
 */
export const getClientLevelList = async (level) => {
  try {
    loadText('Regulations...');
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + level,
      headers: SP_HEADER(),
    });

    const data = res.data.data;

    return data && data.length > 0
      ? data
          .map((level) => ({
            ID: Number(level.id),
            Title: level.title,
            Sort: level.sort,
          }))
          .sort((a, b) => (a.Sort > b.Sort ? 1 : -1))
      : [];
  } catch (error) {
    console.log('EXC in getClientLevelList\n', error.message);
    return [];
  }
};

/**
 * Fetches the organisation structure * @returns The organisation structure.
 */
export const getClientStructure = async () => {
  try {
    loadText('Document Structures...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_CLIENT,
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    return data && data.length > 0
      ? {
          LevelOne: data[0].levelOne,
          LevelTwo: data[0].levelTwo,
          LevelThree:
            data[0].levelThree && data[0].levelThree.length > 0
              ? data[0].levelThree
              : null,
          LevelFour:
            data[0].levelFour && data[0].levelFour.length > 0
              ? data[0].levelFour
              : null,
        }
      : [];
  } catch (error) {
    console.log('EXC in getClientStructure', error.message);
    return [];
  }
};

export const getLogo = async () => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_LOGO,
      headers: SP_HEADER(),
    });
    if (apiResponse) return apiResponse.data.data.logo;
    else return '';
  } catch (error) {
    console.log('EXC in getLogo', error.message);
    return '';
  }
};

/**
 * Assembles the organizing structure of a document
 * @param {*} document The document to be constructed
 * @returns {Promise<{
 * levelOne: { ID: number, Title: string, Sort: number},
 * levelTwo: { ID: number, Title: string, Sort: number},
 * levelThree?: { ID: number, Title: string, Sort: number},
 * levelFour?: { ID: number, Title: string, Sort: number}
 * }>} The document's organization structures
 */
const getDocumentLevels = async (document) => {
  try {
    const client = store.getState().client;

    let levelsObj = {};

    if (client.levelOneList && client.levelOneList.length > 0) {
      levelsObj.levelOne = client.levelOneList.filter(
        (lvl1) => lvl1.ID === Number(document.level_one_id)
      )[0];
    } else {
      levelsObj.levelOne = await getDocumentLevelById(
        Number(document.level_one_id),
        API_LEVEL_ONE
      );
    }

    if (client.levelTwoList && client.levelTwoList.length > 0) {
      levelsObj.levelTwo = client.levelTwoList.filter(
        (lvl2) => lvl2.ID === Number(document.level_two_id)
      )[0];
    } else {
      levelsObj.levelTwo = await getDocumentLevelById(
        Number(document.level_two_id),
        API_LEVEL_TWO
      );
    }

    // optional level
    if (document.level_three_id) {
      if (client.levelThreeList && client.levelThreeList.length > 0) {
        levelsObj.levelThree = client.levelThreeList.filter(
          (lvl3) => lvl3.ID === Number(document.level_three_id)
        )[0];
      } else {
        levelsObj.levelThree = await getDocumentLevelById(
          Number(document.level_three_id),
          API_LEVEL_THREE
        );
      }
    } else levelsObj.levelThree = null;

    // optional level
    if (document.level_four_id) {
      if (client.levelFourList && client.levelFourList.length > 0) {
        levelsObj.levelFour = client.levelFourList.filter(
          (lvl4) => lvl4.ID === Number(document.level_four_id)
        )[0];
      } else {
        levelsObj.levelFour = await getDocumentLevelById(
          Number(document.level_four_id),
          API_LEVEL_FOUR
        );
      }
    } else levelsObj.levelFour = null;

    return levelsObj;
  } catch (e) {
    console.log('Error while trying to fetch levels', e);
    return null;
  }
};

/**
 * Fetch documents from WorkingDocuments list * @returns The array of published documents
 */
export const getClientDocuments = async () => {
  try {
    loadText('Loading RegulationCity...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENTS,
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    // TODO INVESTIGATE IF FASTER LIKE THIS OR WITH HOOKS
    // ON FRONTEND TO DETERMINE IF LISTS ARE POPULATED

    const availableDocs = data && data.length > 0 ? data : [];

    const documents = [];

    for (const document of availableDocs) {
      // finding duplicate slugs
      // if (publishedDocs.some((doc) => doc.slug === document.slug && doc.id !== document.id)) {
      //   console.log('\n\n\n duplicates:', publishedDocs.filter((doc) => doc.slug === document.slug));
      // }

      // console.log(document.slug);

      const levels = await getDocumentLevels(document);

      if (!levels || !levels.levelOne || !levels.levelTwo) {
        // console.log('Mandatory Levels not found for document', document);
        console.log('Mandatory Levels not found for document');
      } else {
        documents.push({
          id: document.doc_id,
          title: document.title,
          slug: document.slug,
          fileName: document.filename,
          levelOne: levels.levelOne,
          levelTwo: levels.levelTwo,
          levelThree: levels.levelThree,
          levelFour: levels.levelFour,
          version:
            !document.document_version || document.document_version === '-'
              ? null
              : Number(document.document_version),
          customTags: document.keywords ? document.keywords.split(',') : [],
          status: document.status,
          // versioning
          newVersionId: document.new_version_id,
          previousVersionsList: [],
          supersededDate: document.supersededDate
            ? new Date(document.supersededDate)
            : null,
          // publishedDate: new Date(document.PublishedDate),
          // regCity specific
          versionDate: document.version_date
            ? new Date(document.version_date)
            : null,
          registrationDate: document.registration_date
            ? new Date(document.registration_date)
            : null,
          createdDate: document.created ? new Date(document.created) : null,
        });
      }
    }

    // iterate through superseded documents
    documents
      .filter((Doc) => Doc.newVersionId !== null && Doc.status === 'Superseded')
      .forEach((Doc) => {
        // locate its new version document in list
        const parentItem = documents.filter(
          (newVersionDoc) => newVersionDoc.id === Doc.newVersionId
        )[0];

        if (parentItem) {
          // find its index
          const parentIndex = documents.indexOf(parentItem);
          // insert
          documents[parentIndex].previousVersionsList.push(Doc);
          documents[parentIndex].previousVersionsList.sort((a, b) =>
            a.supersededDate < b.supersededDate ? 1 : -1
          );
        }
      });
    return documents.filter((doc) => doc.status === 'Published');
  } catch (error) {
    console.log('EXC in getClientDocuments', error.message);
    return [];
  }
};

export const getDocsBookmarks = async () => {
  try {
    loadText('Loading RegulationCity...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENTS,
      headers: SP_HEADER(),
    });
    const data = apiResponse.data.data;
    const availableDocs = data && data.length > 0 ? data : [];
    const documents = [];

    for (const document of availableDocs) {
      const levels = await getDocumentLevels(document);

      if (!levels || !levels.levelOne || !levels.levelTwo) {
        // console.log('Mandatory Levels not found for document', document);
        console.log('Mandatory Levels not found for document');
      } else {
        documents.push({
          id: document.doc_id,
          title: document.title,
          slug: document.slug,
          levelOne: levels.levelOne,
          levelOneTitle: levels.levelOne.Title,
          levelTwoTitle: levels.levelTwo.Title,
          status: document.status,
          // versioning
          versionDate: document.version_date
            ? new Date(document.version_date)
            : null,
          newVersionId: document.new_version_id,
          previousVersionsList: [],
          supersededDate: document.supersededDate
            ? new Date(document.supersededDate)
            : null,
        });
      }
    }
    return documents;
  } catch (error) {
    console.log('Error in get bookmarks documents', error.message);
    return [];
  }
};

// const duplicateSlugRegex = new RegExp(/(-\d+)/gmi);

export const getClientPages = async () => {
  try {
    // loadText('All pages...');
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_PAGES,
      headers: SP_HEADER(),
    });

    const data = res.data.data;

    let documents = store.getState().client.documents;
    if (!documents) documents = await getClientDocuments();

    return data && data.length > 0
      ? data
          .filter(
            (page) =>
              documents.filter((doc) => doc.id === page.doc_id).length > 0
          )
          .map((page) => {
            // detecting duplicate slugs
            // if (data.some((PAGE) => PAGE.slug === page.slug && PAGE.id !== page.id && PAGE.doc_id === page.doc_id)) {
            //   console.log(
            //     'doc:', page.doc_name, '\n',
            //     'pages:',
            //     data.filter((Page) => Page.slug === page.slug && Page.doc_id === page.doc_id).map((p) => p.title)
            //   );
            // console.log('\n\npage', page.title);
            // data
            //   .filter((p) => p.slug === page.slug && p.doc_id === page.doc_id)
            //   .forEach((pageWithIndexedSlug, index) => {
            //     // add indexes from the second one forward
            //     if (index > 0) {
            //       // find elemnent in whole array
            //       const i = data.findIndex(x => x.id === pageWithIndexedSlug.id);
            //       // construct string
            //       const newSlug = `${data[i].slug}-${index}`;
            //       console.log('page', pageWithIndexedSlug, 'now has slug', newSlug);
            //       // insert at right position
            //       data[i].slug = newSlug;
            //     }
            //   });
            // }

            if (page.content.match('<img')) {
              // replace images location
              page.content = page.content.replace(
                new RegExp(clearString(IMAGES_FOLDER), 'gi'),
                `${config.WordPressURL}wp-content/uploads/2022/03/`
              );
            }

            return {
              id: page.page_id,
              title: page.title,
              slug: page.slug,
              parentId:
                page.page_parent_id && page.page_parent_id.length > 0
                  ? page.page_parent_id
                  : null,
              tags: page.tags,
              documentName: page.doc_name,
              documentId: page.doc_id,
              documentSlug: documents.filter((doc) => doc.id === page.doc_id)[0]
                ? documents.filter((doc) => doc.id === page.doc_id)[0].slug
                : null,
              HTMLContent: page.content.replace(/href="#/gi, 'href="/app'),
            };
          })
      : [];
  } catch (error) {
    console.log('EXC in getPages\n', error.message);
    return [];
  }
};

/**
 *
 * @param {{singular: string,plural: string,subString: boolean}} searchObj
 * @returns
 */
export const search = async (searchObj) => {
  try {
    // console.log('searching', searchObj);
    const res = await axios.post(
      BASE_URL + API_SEARCH,
      JSON.stringify(searchObj),
      {
        headers: SP_HEADER(),
      }
    );

    const data = res.data.data;

    // console.log('found', data);

    let documents = store.getState().client.documents;
    if (!documents) documents = await getClientDocuments();

    return data && data.length > 0
      ? data
          .filter(
            (page) =>
              documents.filter((doc) => doc.id === page.doc_id).length > 0
          )
          .map((page) => {
            if (
              typeof page.content === 'string' &&
              page.content.match('<img')
            ) {
              // replace images location
              page.content = page.content.replace(
                new RegExp(clearString(IMAGES_FOLDER), 'gi'),
                `${config.WordPressURL}wp-content/uploads/2022/03/`
              );
            }

            const doc = documents.filter((doc) => doc.id === page.doc_id)[0];

            return {
              id: page.page_id,
              title: page.title,
              slug: page.slug,
              parentId:
                page.page_parent_id && page.page_parent_id.length > 0
                  ? page.page_parent_id
                  : null,
              tags: page.tags,
              documentName: page.doc_name,
              documentId: page.doc_id,
              documentSlug: doc.slug ? doc.slug : null,
              HTMLContent:
                typeof page.content === 'string' &&
                page.content.replace(/href="#/gi, 'href="/app'),
              // document attributes
              versionDate: doc.versionDate,
              levelOne: doc.levelOne.Title,
              levelTwo: doc.levelTwo.Title,
              levelThree: doc.levelThree ? doc.levelThree.Title : null,
              levelFour: doc.levelFour ? doc.levelFour.Title : null,
            };
          })
      : [];
  } catch (error) {
    console.log('EXC in search\n', error.message);
    return [];
  }
};

export const getClientRightNavigation = async () => {
  try {
    loadText('Navigation Items...');
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_NAVIGATION,
      headers: SP_HEADER(),
    });

    const data = res.data.data;

    if (data) return data[0].title.split(',');
    else return [];
  } catch (error) {
    console.log('EXC in GetRightNavContent', error.message);
    return [];
  }
};

export const getGlossaries = async () => {
  try {
    loadText('Glossary...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_GLOSSARY,
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    if (data && data.length > 0) {
      const glossaryList = [];

      data.forEach((glossaryObj) => {
        if (glossaryObj.glossary_content) {
          const contentAsList = glossaryObj.glossary_content
            .replace(/{|}/g, '')
            .split(`","`);

          const contentArray = [];
          contentAsList.forEach((item, i) => {
            if (
              !(
                i === 0 &&
                item.split(`":"`)[0].match('Term') &&
                item.split(`":"`)[1].match('Definition')
              )
            )
              contentArray.push({
                Key: item.split(`":"`)[0].replace(/"|{|}/g, ''),
                Definition: item
                  .split(`":"`)[1]
                  .replace(/"|{|}/g, '')
                  .replace(/\\t/g, '&emsp;'),
              });
          });
          glossaryList.push({
            ID: Number(glossaryObj.id),
            GlossaryName: glossaryObj.glossary_name,
            Content: contentArray,
          });
        }
      });

      return glossaryList;
    } else {
      return [];
    }
  } catch (error) {
    console.log('EXC in getGlossary', error.message);
    return [];
  }
};

// Get Glossary relationships
export async function getGlossaryRelationships(glossaries) {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_GLOSSARY_RELATIONSHIP,
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    let relationshipsArray = [];

    if (data && data.length > 0) {
      try {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                GlossaryId: Number(relationship.glossary_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      } catch (e) {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                GlossaryId: Number(relationship.glossary_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      }
    }

    // mapping the relationships to the items
    return relationshipsArray.length > 0
      ? glossaries.map((glossary) => ({
          ...glossary,
          Relationships: relationshipsArray
            .filter((relationship) => relationship.GlossaryId === glossary.ID)
            .map((relationship) => ({
              ...relationship,
              TargetValue:
                // document ids are strings, while level ids are numbers
                relationship.TargetType === 'Document' ||
                relationship.TargetType === 'Global'
                  ? relationship.TargetValue
                  : Number(relationship.TargetValue),
            })),
        }))
      : [];
  } catch (error) {
    console.log('getRelatedContents ERROR', error);
    return [];
  }
}

// Get Related Contents
export async function getRelatedContents() {
  try {
    loadText('Your Related Content...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_RELATED_CONTENT,
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    try {
      return data
        ? data.map((content) => ({
            ID: Number(content.id),
            Title: content.title,
            UploadContent: content.upload_content,
            ContentType: content.content_type,
            RelatedContentUrl: content.related_content_url,
          }))
        : [];
    } catch (e) {
      return data
        ? data.map((content) => ({
            Title: content.title,
            UploadContent: content.upload_content,
            ContentType: content.content_type,
            RelatedContentUrl: content.related_content_url,
          }))
        : [];
    }
  } catch (error) {
    console.log('getRelatedContents ERROR', error);
    return [];
  }
}

// Get Related Content relationships
export async function getRelatedContentsRelationships(contents) {
  try {
    // loadText('Related Content Structures...');

    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_BASE + API_RELATED_CONTENT_RELATIONSHIP,
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    let relationshipsArray = [];

    if (data && data.length > 0) {
      try {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                RelatedContentId: Number(relationship.relationship_content_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      } catch (e) {
        relationshipsArray =
          data && data.length > 0
            ? data.map((relationship) => ({
                RelatedContentId: Number(relationship.relationship_content_id),
                TargetType: relationship.target_type,
                TargetValue: relationship.target_value,
              }))
            : [];
      }
    }
    // mapping the relationships to the items
    return relationshipsArray.length > 0
      ? contents.map((content) => ({
          ...content,
          Relationships: relationshipsArray
            .filter(
              (relationship) => relationship.RelatedContentId === content.ID
            )
            .map((relationship) => ({
              ...relationship,
              TargetValue:
                // document ids are strings, while levelOne and Two ids are numbers
                relationship.TargetType === 'Document' ||
                relationship.TargetType === 'Global'
                  ? relationship.TargetValue
                  : Number(relationship.TargetValue),
            })),
        }))
      : [];
  } catch (error) {
    console.log('getRelatedContents ERROR', error);
    return [];
  }
}

// pages: doc.contents.map((page) => {
//   let newHTML = page.content;

//   if (newHTML.includes("https://adminwordflow.sharepoint.com")) {
//     newHTML = newHTML.replace(/https:[/][/]adminwordflow[.]sharepoint[.]com[/]sites[/]1WordFlow-Sandbox[/]PublishingImages[/]/gi, `${config.WordPressURL}wp-content/uploads/2021/05/`)
//   }

//   return ({
//     HTMLContent: newHTML,
//     documentId: page.doc_id,
//     documentName: page.doc_name,
//     id: page.page_id,
//     parentId: page.page_parent_id,
//     tags: page.tags,
//     title: page.title.replace(`&#8211;`, '-').replace(
//       `&#8217;`,
//       `'` // logic to properly replace single quote and dash
//     ),
//   });
// }),

//#endregion

//#region Document Reducer

export const getDocumentBySlug = async (slug) => {
  try {
    loadText('Selected Document...');
    let document = null;

    const slugRes = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENT_BY_SLUG(slug),
      headers: SP_HEADER(),
    });

    // if user tries to open document ID from URL somehow
    if (
      slugRes.data.message === 'There is no such url' ||
      slugRes.data.status !== 200
    ) {
      const IDRes = await axios({
        method: 'GET',
        baseURL: BASE_URL,
        url: API_DOCUMENT_BY_ID(slug.toUpperCase()),
        headers: SP_HEADER(),
      });

      if (IDRes.status === 200) {
        document =
          IDRes.data.data && IDRes.data.data.length > 0
            ? IDRes.data.data[0]
            : null;
      }
    } else {
      document =
        slugRes.data.data && slugRes.data.data.length > 0
          ? slugRes.data.data[0]
          : null;
    }

    if (document === null) throw new Error('Document not found');

    const levels = await getDocumentLevels(document);

    if (!levels || !levels.levelOne || !levels.levelTwo)
      throw new Error('Mandatory Levels not found');

    const previousVersionsList = await getPreviousVersions(document);

    return {
      id: document.doc_id,
      title: document.title,
      slug: document.slug,
      fileName: document.filename,
      levelOne: levels.levelOne,
      levelTwo: levels.levelTwo,
      levelThree: levels.levelThree,
      levelFour: levels.levelFour,
      version:
        !document.document_version || document.document_version === '-'
          ? null
          : Number(document.document_version),
      status: document.status,
      customTags: document.keywords ? document.keywords.split(',') : [],

      newVersionId: document.new_version_id,
      previousVersionsList: previousVersionsList,
      supersededDate: document.supersededDate
        ? new Date(document.supersededDate)
        : null,
      // publishedDate: new Date(document.PublishedDate),
      // regCity specific
      versionDate: document.version_date
        ? new Date(document.version_date)
        : null,
      registrationDate: document.registration_date
        ? new Date(document.registration_date)
        : null,
      summary: await getDocSummary(document.doc_id),
    };
  } catch (e) {
    console.log('ERROR ON getDocumentBySlug', e);
    return 'Error';
  }
};

/**
 * Fetch previous versions from WorkingDocuments list by document ID.
 * (Used if a current document is set before all the documents have been loaded.)
 * @param {object} doc The document to be checked. * @returns The array of superseded documents
 */
export const getPreviousVersions = async (doc) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_DOCUMENT_BY_NEW_VERSION_ID(doc.doc_id),
      headers: SP_HEADER(),
    });

    // request returned at least one item
    if (
      apiResponse &&
      apiResponse.data &&
      apiResponse.data.data &&
      apiResponse.data.data.length > 0
    ) {
      const supersededDocuments = apiResponse.data.data;
      const documents = [];

      for (const document of supersededDocuments) {
        const levels = await getDocumentLevels(document);

        if (!levels || !levels.levelOne || !levels.levelTwo) {
          console.log('Mandatory Levels not found for document', document);
        } else {
          documents.push({
            id: document.doc_id,
            title: document.title,
            slug: document.slug,
            fileName: document.filename,
            levelOne: levels.levelOne,
            levelTwo: levels.levelTwo,
            levelThree: levels.levelThree,
            levelFour: levels.levelFour,
            version:
              !document.document_version || document.document_version === '-'
                ? null
                : Number(document.document_version),
            customTags: document.keywords ? document.keywords.split(',') : [],
            status: document.status,
            // versioning
            newVersionId: document.new_version_id,
            previousVersionsList: [],
            supersededDate: document.supersededDate
              ? new Date(document.supersededDate)
              : null,
            // publishedDate: new Date(document.PublishedDate),
            // regCity specific
            versionDate: document.version_date
              ? new Date(document.version_date)
              : null,
            registrationDate: document.registration_date
              ? new Date(document.registration_date)
              : null,
          });
        }
      }
      return documents;
    } else {
      return [];
    }
  } catch (error) {
    console.log('EXC in getPreviousVersions', error.message);
    return [];
  }
};

export const getPagesByDocumentID = async (docID, docSlug) => {
  try {
    loadText('Document pages...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_PAGES_BY_DOC(docID),
      headers: SP_HEADER(),
    });

    const res = apiResponse.data.data;

    // console.log('getPagesByDocumentID', res);

    return res && res.length > 0
      ? res.map((page) => {
          if (page.content.includes('https://adminwordflow.sharepoint.com')) {
            // replace images location
            page.content = page.content.replace(
              new RegExp(clearString(IMAGES_FOLDER), 'gi'),
              `${config.WordPressURL}wp-content/uploads/2022/03/`
            );
          }

          return {
            id: page.page_id,
            title: page.title,
            slug: page.slug,
            parentId:
              page.page_parent_id && page.page_parent_id.length > 0
                ? page.page_parent_id
                : null,
            tags: page.tags,
            documentName: page.doc_name,
            documentId: page.doc_id,
            documentSlug: docSlug,
            HTMLContent: page.content.replace(/href="#/gi, 'href="/app'),
          };
        })
      : [];
  } catch (e) {
    console.log('Error while extracting document pages', e);
    return [];
  }
};

/**
 * Get a specific level item via its Lookup id
 * @param {number} id The id of the level within the list
 * @param {string} level The list to be queried * @returns The Level object with title and sort.
 */
const getDocumentLevelById = async (id, level) => {
  try {
    const res = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_GET_LIST_BY_ID(level, id),
      headers: SP_HEADER(),
    });

    if (res.status === 200 && res.data.data.length > 0) {
      const LevelsResponse = res.data.data;

      // select the according level if api returns the whole list
      const Level =
        LevelsResponse.length === 1
          ? LevelsResponse[0]
          : LevelsResponse.filter((level) => Number(level.id) === id)[0];

      return {
        ID: Number(Level.id),
        Title: Level.title,
        Sort: Level.sort,
      };
    } else return null;
  } catch (e) {
    console.log('Error while trying to fetch level', e);
    return null;
  }
};

// Generate navigation based on pages
export const generateNavigation = (pages) => {
  try {
    const navigation = [];
    pages.forEach((page) => {
      const hasParent = page.parentId !== null;
      const hasGrandparent =
        hasParent &&
        pages.filter((parentPage) => parentPage.id === page.parentId)[0]
          .parentId !== null;
      // H1 only
      if (!hasParent) {
        const item = {
          id: page.id,
          slug: page.slug,
          title: page.title,
          children: [],
          content: page.HTMLContent,
        };
        navigation.push(item);
      } // H3 only
      else if (hasParent && hasGrandparent) {
        // find gparent index
        const H1 = navigation.filter(
          (grandParentItem) =>
            grandParentItem.id ===
            pages.filter((parentPage) => parentPage.id === page.parentId)[0]
              .parentId
        )[0];
        if (H1) {
          const H1Index = navigation.indexOf(H1);
          // find parent index in gparent
          const H2 = navigation[H1Index].children.filter(
            (h2) => h2.id === page.parentId
          )[0];
          const H2Index = navigation[H1Index].children.indexOf(H2);

          const H3 = {
            id: page.id,
            slug: page.slug,
            title: page.title,
            content: page.HTMLContent,
          };

          // insert
          navigation[H1Index].children[H2Index].children.push(H3);
        }
      } else {
        const parentItem = navigation.filter(
          (parentPage) => parentPage.id === page.parentId
        )[0];
        if (parentItem) {
          const child = {
            id: page.id,
            slug: page.slug,
            title: page.title,
            content: page.HTMLContent,
            children: [],
          };
          const index = navigation.indexOf(parentItem);
          navigation[index].children.push(child);
        }
      }
    });
    return navigation;
  } catch (error) {
    console.log('ERROR', error);
  }
};

export const getRelatedDocuments = async (docId) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_RELATED_DOCUMENT + docId,
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    if (data && data.length > 0) {
      const documents = store.getState().client.documents;

      const relatedDocuments = data.map((relatedDocument) => ({
        isParent: docId === relatedDocument.parent_id,
        doc1Title: documents.filter(
          (doc) => doc.id === relatedDocument.parent_id
        )[0]
          ? documents.filter((doc) => doc.id === relatedDocument.parent_id)[0]
              .title
          : '',
        doc1Id: relatedDocument.parent_id,
        doc1Slug: getSlugFromIDs(relatedDocument.parent_id).docSlug,
        doc2Title: relatedDocument.title,
        doc2Id: relatedDocument.child_id,
        doc2Slug: getSlugFromIDs(relatedDocument.child_id).docSlug,
      }));

      return relatedDocuments.filter((doc) => doc.doc1Title.length > 0);
    } else return [];
  } catch (error) {
    console.log('EXC in getRelatedDocuments', error.message);
    return [];
  }
};

export const getRelatedSections = async (pageId) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_RELATED_SECTION + pageId,
      headers: SP_HEADER(),
    });

    if (
      apiResponse &&
      apiResponse.data.data &&
      apiResponse.data.data.length >= 0
    )
      return apiResponse.data.data.map((relatedPage) => ({
        RelatedDocumentId: relatedPage.related_document_id,

        RelatedDocumentSlug: getSlugFromIDs(relatedPage.related_document_id)
          .docSlug,

        RelatedPageId: relatedPage.related_page_id,

        RelatedPageSlug: getSlugFromIDs(null, relatedPage.related_page_id)
          .pageSlug,

        RelatedPageTitle: relatedPage.related_page_title,
      }));
    else return [];
  } catch (error) {
    console.log('EXC in getRelatedSections', error.message);
    return [];
  }
};

// Logic to only save unique relationships (if empty parent and child are both related to same page)
export const getRelationships = async (page, documentPages) => {
  if (page && documentPages && documentPages.length > 1) {
    let parentRelatedPagesList = [];
    // check if parent is empty and if it is, fetch its related pages too
    if (
      page.parentId !== null && // has a parent page
      documentPages.filter(
        (documentPage) => documentPage.parentId === page.parentId
      )[0].id === page.id && // is the first child of parent
      !pageHasContent(
        // parent page is empty
        documentPages.filter(
          (documentPage) => documentPage.id === page.parentId
        )[0].HTMLContent
      )
    )
      parentRelatedPagesList = await getRelatedSections(page.parentId);

    const relatedPages = await getRelatedSections(page.id);

    const allRelations = [...parentRelatedPagesList, ...relatedPages];
    const uniqueIDs = [];

    allRelations.forEach((relation) => uniqueIDs.push(relation.RelatedPageId));

    const uniqueRelations = [];

    uniqueIDs
      .filter((value, index) => uniqueIDs.indexOf(value) === index)
      .forEach((uniqueRelatedPageID) =>
        uniqueRelations.push(
          allRelations.filter(
            (relPages) => relPages.RelatedPageId === uniqueRelatedPageID
          )[0]
        )
      );

    return uniqueRelations;
  } else if (page && documentPages && documentPages.length === 1) {
    const relatedPages = await getRelatedSections(page.id);
    return relatedPages;
  } else return null;
};

//#endregion

//#region User Reducer

export const logInFromToken = async () => {
  try {
    // console.log('logInFromToken');
    const res = await axios.post(AUTH_BASE + REFRESH_TOKEN_API, null, {
      headers: HEADER,
    });

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;

      // console.log('login', res);

      return data.succeeded === true
        ? data.data
        : {
            success: false,
            reason: data.message,
            request: LOG_IN,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: LOG_IN,
    };
  }
};

/**
 * Adds a product line (free tier) to the logged-in user's EXISTING account.
 * Idempotent server-side. After success, refresh the auth state (logIn(null, null))
 * so the new entitlement lands in the JWT and the user object.
 * @param {string} productLine 'ReaderApp' | 'DeveloperApi'
 * @returns Response<SubscriptionInfo> shape: { succeeded, message, data }
 */
export const activateProduct = async (productLine) => {
  try {
    const res = await axios.post(
      AUTH_BASE + ACTIVATE_PRODUCT_API,
      JSON.stringify({ productLine }),
      {
        headers: SP_HEADER(),
      }
    );

    if (res && res.data) return res.data;
    else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      succeeded: false,
      message:
        e.response && e.response.data && e.response.data.Message
          ? e.response.data.Message
          : e.message,
    };
  }
};

export const tryLogin = async (email, password) => {
  try {
    const data = {
      email: email,
      password: password,
    };

    const res = await axios.post(
      AUTH_BASE + AUTHENTICATE,
      JSON.stringify(data),
      {
        headers: HEADER,
      }
    );

    if (res && res.data) {
      const { data } = res;

      // console.log('login', res);

      return data.succeeded === true
        ? data.data
        : {
            success: false,
            reason: data.message,
            userID: data.data && data.data.id ? data.data.id : null,
            request: LOG_IN,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: LOG_IN,
    };
  }
};

export const revokeToken = async () => {
  try {
    // console.log('logInFromToken');
    const res = await axios.post(
      AUTH_BASE + LOG_OUT_API,
      JSON.stringify({
        token: null,
      }),
      {
        headers: HEADER,
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;

      // console.log('login', res);

      return data.succeeded === true
        ? data
        : {
            success: false,
            reason: data.message,
            request: LOG_OUT,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: LOG_OUT,
    };
  }
};

export const sendCancelReason = async (reasonObj) => {
  try {
    const token = store.getState().user.user.token;
    const userID = store.getState().user.user.userID;

    reasonObj = {
      userId: userID,
      ...reasonObj,
    };

    const res = await axios.post(
      AUTH_BASE + CANCEL_REASON_API,
      JSON.stringify(reasonObj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log(res);

    if (res && res.data) {
      const { data } = res;

      if (data.succeeded === true) {
        notification('success', 'Thank you for your feedback.');
      } else if (
        typeof data === 'string' &&
        data.match('The token is expired')
      ) {
        TokenExpiredNotification();
      }
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // if (e.response || e.message) {
    //   const reason = getErrReason(e);
    //   if (reason.match('The token is expired')) {
    //     TokenExpiredNotification();
    //   } else {
    //     notification('info', 'Sorry, could not save your response.', reason);
    //   }
    // }
    console.log('Error saving reason for cancellation.', e);
  }
};

export const forgotPassword = async (email) => {
  try {
    const res = await axios.post(
      AUTH_BASE + FORGOT_PASSWORD,
      JSON.stringify({
        email: email,
      }),
      {
        headers: HEADER,
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            email: data.data,
            sent: true,
            reason: null,
          }
        : {
            email: email,
            sent: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      email: email,
      sent: false,
      reason: e.response ? e.response.data.Message : e.message,
    };
  }
};

export const resetPassword = async (
  email,
  token,
  password,
  confirmPassword
) => {
  try {
    const data = {
      email: email,
      token: token,
      password: password,
      confirmPassword: confirmPassword,
    };

    const res = await axios.post(
      AUTH_BASE + RESET_PASSWORD,
      JSON.stringify(data),
      {
        headers: HEADER,
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            message:
              'Password Reset Successful! You may login with your new password.',
            success: true,
            reason: null,
          }
        : {
            success: false,
            reason: data.message,
            request: RESET_PASS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('Error', e.response);
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: RESET_PASS,
    };
  }
};

export const changePassword = async (passObj, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CHANGE_PASSWORD_API,
      JSON.stringify(passObj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('changePassword', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: UPDATE_PASS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('Error', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: UPDATE_PASS,
    };
  }
};

export const updateUser = async (user, token) => {
  try {
    const res = await axios.put(
      AUTH_BASE + UPDATE_USER_API(user.id),
      JSON.stringify(user),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res update user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.Message,
            request: UPDATE_USER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('error', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: UPDATE_USER,
    };
  }
};

export const register = async (user) => {
  try {
    const res = await axios.post(
      AUTH_BASE + REGISTER_API,
      JSON.stringify(user),
      {
        headers: HEADER,
      }
    );

    // console.log('register', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            userID: data.data,
            firstName: user.firstName,
            email: user.email,
            success: true,
            reason: null,
            request: REGISTER,
          }
        : {
            success: false,
            reason: data.message,
            request: REGISTER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: REGISTER,
    };
  }
};

export const resendConfirmEmail = async (userId, email) => {
  try {
    const res = await axios.get(AUTH_BASE + RESEND_EMAIL_API(userId), {
      headers: HEADER,
    });

    // console.log('resend confirm email', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            email: email,
            success: true,
            reason: null,
            request: RESEND_EMAIL,
          }
        : {
            success: false,
            reason: data.message,
            request: RESEND_EMAIL,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: RESEND_EMAIL,
    };
  }
};

export const confirmEmail = async (userId, code) => {
  try {
    const res = await axios.get(
      AUTH_BASE + CONFIRM_EMAIL_ENDPOINT(userId, code),
      {
        headers: HEADER,
      }
    );

    // console.log('confirm', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: null,
          }
        : {
            success: false,
            reason: data.message,
            request: CONFIRM_EMAIL,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason: e.response ? e.response.data.Message : e.message,
      request: CONFIRM_EMAIL,
    };
  }
};

export const createBookmark = async (bookmarkObj, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_BOOKMARK_API,
      JSON.stringify(bookmarkObj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            newBookmark: {
              id: data.data.id,
              page: data.data.pageId,
              pageTitle: data.data.pageTitle,
              document: data.data.documentId,
            },
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: CREATE_BOOKMARK,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        getErrReason(e),
      request: CREATE_BOOKMARK,
    };
  }
};

export const deleteBookmark = async (bookmark, token) => {
  try {
    const res = await axios.delete(AUTH_BASE + DELETE_BOOKMARK_API(bookmark), {
      headers: AUTH_HEADER(token),
    });

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_BOOKMARK,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        getErrReason(e),
      request: DELETE_BOOKMARK,
    };
  }
};

export const deleteBookmarks = async (bookmarksList, token) => {
  try {
    const data = bookmarksList.map((bookmark) => bookmark.id);

    const res = await axios.post(
      AUTH_BASE + DELETE_BOOKMARKS_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_BOOKMARKS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        getErrReason(e),
      request: DELETE_BOOKMARKS,
    };
  }
};

export const getUserBookmarks = async (userId, token) => {
  try {
    const res = await axios.get(AUTH_BASE + GET_USER_BOOKMARKS(userId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('get bookmarks', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? data.data.map((bookmark) => ({
            id: bookmark.id,
            page: bookmark.pageId,
            pageTitle: bookmark.pageTitle,
            document: bookmark.documentId,
          }))
        : {
            success: false,
            reason: data.message,
            request: GET_BOOKMARKS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log('getUserBookmarks', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: GET_BOOKMARKS,
    };
  }
};

export const createComment = async (commentObj) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_COMMENT_API,
      JSON.stringify(commentObj),
      {
        headers: HEADER,
      }
    );

    console.log('comments', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      getErrReason(e)
    );
    return {
      success: false,
      reason:
        getErrReason(e),
    };
  }
};

// invite user to corporate account
export const inviteUser = async (newUser, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + INVITE_USER_API,
      JSON.stringify(newUser),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('invite user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            newUser: {
              id: data.data,
              name: `${newUser.firstName} ${newUser.lastName}`,
              email: newUser.email,
              role: newUser.role === 'CorporateAdmin' ? 'Admin' : 'Basic',
              isAdmin: newUser.role === 'CorporateAdmin' ? true : false,
              accountActive: true,
            },
            success: true,
            reason: null,
            request: INVITE_USER,
          }
        : {
            success: false,
            reason: data.message,
            request: INVITE_USER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        getErrReason(e),
      request: INVITE_USER_ERROR,
    };
  }
};

export const getUserByClientId = async (clientId, token) => {
  try {
    const res = await axios.get(
      AUTH_BASE + GET_USERS_BY_CLIENT_ID_API(clientId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('getUserByClientId', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? data.data.map((userByClient) => ({
            id: userByClient.id,
            name: `${userByClient.firstName} ${userByClient.lastName}`,
            email: userByClient.email,
            role: userByClient.roles.includes('CorporateAdmin')
              ? 'Admin'
              : 'Viewer',
            isAdmin: userByClient.roles.some((role) => {
              if (role === 'CorporateAdmin') return true;
              else return false;
            }),
            accountActive: userByClient.active,
            ...userByClient.userByClient,
          }))
        : {
            success: false,
            reason: data.message,
            request: GET_USERS_BY_CLIENT_ID,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('getUserByClientId ERROR', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: GET_USERS_BY_CLIENT_ID,
    };
  }
};

export const totalUsersByClient = async (clientId, token) => {
  try {
    const res = await axios.get(
      AUTH_BASE + GET_USERS_BY_CLIENT_ID_API(clientId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('getUserByClientId', res);

    if (res && res.data) {
      const { data } = res;
      return res.data.succeeded === true
        ? data.data
        : {
            success: false,
            reason: res.message,
            request: GET_USERS_BY_CLIENT_ID,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('getUserByClientId ERROR', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: GET_USERS_BY_CLIENT_ID,
    };
  }
};

export const deleteUser = async (clientId, userId, token) => {
  try {
    const res = await axios.delete(
      AUTH_BASE + DELETE_USER_CLIENT_API(clientId, userId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('delete user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            request: DELETE_USER_CLIENT,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_USER_CLIENT,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('delete user', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: DELETE_USER_CLIENT_API,
    };
  }
};

export const updateClientRole = async (data, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + UPDATE_CLIENT_ROLE_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('update role', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: data.message,
            request: UPDATE_CLIENT_ROLE,
          }
        : {
            success: false,
            reason: data.message,
            request: UPDATE_CLIENT_ROLE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log('error', e);
    return {
      success: false,
      reason:
        !e.response.data.message || !e.message
          ? e.response.data
          : e.response
          ? e.response.data.Message
          : e.message,
      request: UPDATE_CLIENT_ROLE,
    };
  }
};

export const reinviteUser = async (email, clientId, token) => {
  try {
    const data = {
      email: email,
      clientId: clientId,
    };

    const res = await axios.post(
      AUTH_BASE + REINVITE_USER_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('reinvite user', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: data.message,
            request: REINVITE_USER,
          }
        : {
            success: false,
            reason: data.message,
            request: REINVITE_USER,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(e);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: REINVITE_USER_ERROR,
    };
  }
};

export const sendEnquiry = async (quote) => {
  try {
    const res = await axios.post(
      AUTH_BASE + SEND_ENQUIRY_API,
      quote
      // {
      //   headers: AUTH_HEADER(token),
      // }
    );

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            reason: data.message,
            request: SEND_ENQUIRY,
          }
        : {
            success: false,
            reason: data.message,
            request: SEND_ENQUIRY,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(e);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: SEND_ENQUIRY,
    };
  }
};

export const getDocSummary = async (id) => {
  try {
    loadText('Document Summary...');
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: API_GET_DOC_SUMMARY(id),
      headers: SP_HEADER(),
    });

    const data = apiResponse.data.data;

    return data.filter((doc) => doc.documentId === id)[0].summary;
  } catch (error) {
    console.log('Error in get Sumary', error.message);
    return null;
  }
};

export const getUserClient = async (key, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: UPDATE_CLIENT_API(id),
      headers: AUTH_HEADER(key),
    });

    const data = apiResponse.data.data;
    return data;
  } catch (error) {
    console.log('Error getting user client', error.message);
    return null;
  }
};

// Developer-API monthly usage report for the authenticated caller.
// Returns { year, month, days: [{ day, count }], totalCalls, totalCost } or null.
export const getApiUsage = async (token, year, month) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: API_USAGE_MY(year, month),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error getting API usage', error.message);
    return null;
  }
};

// Admin: cross-product KPI snapshot for the dashboard Overview (SuperAdmin). Returns the stats
// object ({ totalAccounts, apiTrialCount, readerByPlan, ... }) or null on error.
export const getAdminOverview = async (token) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/overview',
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error getting admin overview', error.message);
    return null;
  }
};

// Admin: search/list customer accounts across both product lines (SuperAdmin). `product` optionally
// filters to 'reader' or 'api'. Returns { items: [...], total } or null on error.
export const getAdminAccounts = async (token, search = '', product = '', take = 50) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: `/v1/Admin/accounts?search=${encodeURIComponent(search || '')}&take=${take}${
        product ? `&product=${encodeURIComponent(product)}` : ''
      }`,
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error listing accounts', error.message);
    return null;
  }
};

// Admin: the plan catalogue (both product lines) for dropdowns. Returns an array or null.
export const getAdminPlans = async (token) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/plans',
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error getting plans', error.message);
    return null;
  }
};

// Admin: set/comp a client's reader plan (local override). Returns { success, reason, detail }.
export const setReaderPlan = async (token, clientId, planId) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/set-reader-plan',
      headers: AUTH_HEADER(token),
      data: { clientId, planId },
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      detail: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.response ? error.response.data.Message : error.message,
    };
  }
};

// Admin: update a plan's editable fields (SuperAdmin). `plan` = { id, price, queryLimit,
// allowsApiKeys }. Returns { success, reason, plan }.
export const updatePlan = async (token, plan) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/update-plan',
      headers: AUTH_HEADER(token),
      data: plan,
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      plan: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.response ? error.response.data.Message : error.message,
    };
  }
};

// Admin: cancel a client's reader subscription (local override). Returns { success, reason, detail }.
export const cancelReaderPlan = async (token, clientId) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/cancel-reader-plan',
      headers: AUTH_HEADER(token),
      data: { clientId },
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      detail: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.response ? error.response.data.Message : error.message,
    };
  }
};

// Admin: give a client's App trial more days (a lapsed one is extended from today and regains
// access). Only a trial can be extended. Returns { success, reason, detail }.
export const extendReaderTrial = async (token, clientId, days) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/extend-reader-trial',
      headers: AUTH_HEADER(token),
      data: { clientId, days },
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      detail: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.response ? error.response.data.Message : error.message,
    };
  }
};

// Admin: offboard a client from the Developer API — cancel the sub + revoke their keys. SuperAdmin.
export const cancelApiPlan = async (token, clientId) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/cancel-api-plan',
      headers: AUTH_HEADER(token),
      data: { clientId },
    });
    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      detail: apiResponse.data.data,
    };
  } catch (error) {
    return { success: false, reason: getErrReason(error) };
  }
};

// Admin: downgrade a client's Developer API subscription back to the free Trial tier. SuperAdmin.
export const downgradeApiToTrial = async (token, clientId) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/downgrade-api-trial',
      headers: AUTH_HEADER(token),
      data: { clientId },
    });
    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      detail: apiResponse.data.data,
    };
  } catch (error) {
    return { success: false, reason: getErrReason(error) };
  }
};

// Admin: unified account detail across both product lines, by client id (SuperAdmin). Returns
// { found, clientId, name, email, company, reader:{...}, api:{...} } or null on error.
export const getAdminAccountDetail = async (token, clientId) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: `/v1/Admin/account-detail?clientId=${clientId}`,
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error getting account detail', error.message);
    return null;
  }
};

// Admin: the activity history (audit) for a customer account, newest first (SuperAdmin).
export const getAccountAudit = async (token, clientId) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: `/v1/Admin/account-audit?clientId=${clientId}`,
      headers: AUTH_HEADER(token),
    });
    return apiResponse.data.data || [];
  } catch (error) {
    console.log('Error getting account audit', getErrReason(error));
    return [];
  }
};

// Admin: look up a customer's API account by email (SuperAdmin). Returns the summary object
// ({ found, clientId, planName, monthlyLimit, usedThisMonth, ... }) or null on error.
export const getApiAccountSummary = async (token, email) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: `/v1/Admin/account-summary?email=${encodeURIComponent(email)}`,
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error getting account summary', error.message);
    return null;
  }
};

// Admin: move a client onto the Production API plan (SuperAdmin). Returns {success, reason}.
// Admin: move a client onto the Enterprise (paid "Production") API plan, optionally recording a
// negotiated price. Mirrors provisionReaderEnterprise. `price` null/blank leaves any recorded price.
export const provisionApiProduction = async (token, clientId, price, priceInterval, extra = {}) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/provision-api-production',
      headers: AUTH_HEADER(token),
      data: {
        clientId,
        price: price == null ? null : price,
        priceInterval: priceInterval || null,
        seats: extra.seats == null ? null : extra.seats,
        adminEmail: extra.adminEmail || null,
        adminFirstName: extra.adminFirstName || null,
        adminLastName: extra.adminLastName || null,
        startDate: extra.startDate || null,
        endDate: extra.endDate || null,
        purchaseOrder: extra.purchaseOrder == null ? null : extra.purchaseOrder,
        notes: extra.notes == null ? null : extra.notes,
        allowedDomains: extra.allowedDomains == null ? null : extra.allowedDomains,
      },
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
    };
  } catch (error) {
    return {
      success: false,
      reason: getErrReason(error),
    };
  }
};

// Admin: provision a trial account (creates/attaches the account + issues an API key). SuperAdmin.
// Returns { success, reason, data } where data carries the one-time key/secret/password.
export const provisionApiTrial = async (token, payload) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/provision-api-trial',
      headers: AUTH_HEADER(token),
      data: payload,
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      data: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.response ? error.response.data.Message : error.message,
    };
  }
};

// Admin: set (or clear, with monthlyLimit 0/null) a per-client API call limit, metered over the
// given period ("Monthly"/"Annual", default Monthly). Returns { success, reason, summary }.
export const setApiQuota = async (token, clientId, monthlyLimit, quotaInterval) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/set-api-quota',
      headers: AUTH_HEADER(token),
      data: { clientId, monthlyLimit, quotaInterval: quotaInterval || null },
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      summary: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: getErrReason(error),
    };
  }
};

// Admin: move a reader account onto the Enterprise plan with a negotiated price (SuperAdmin).
// The reader analogue of provisionApiProduction. Returns { success, reason, detail }.
export const provisionReaderEnterprise = async (token, clientId, price, priceInterval, extra = {}) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/provision-reader-enterprise',
      headers: AUTH_HEADER(token),
      data: {
        clientId,
        price,
        priceInterval: priceInterval || null,
        seats: extra.seats == null ? null : extra.seats,
        adminEmail: extra.adminEmail || null,
        adminFirstName: extra.adminFirstName || null,
        adminLastName: extra.adminLastName || null,
        startDate: extra.startDate || null,
        endDate: extra.endDate || null,
        purchaseOrder: extra.purchaseOrder == null ? null : extra.purchaseOrder,
        notes: extra.notes == null ? null : extra.notes,
        allowedDomains: extra.allowedDomains == null ? null : extra.allowedDomains,
      },
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      detail: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: getErrReason(error),
    };
  }
};

// Admin: invite an off-platform prospect onto a reader trial (creates the account + emails a
// set-password link). The reader analogue of provisionApiTrial. Returns { success, reason, data }.
export const provisionReaderTrial = async (token, payload) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: '/v1/Admin/provision-reader-trial',
      headers: AUTH_HEADER(token),
      data: payload,
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
      data: apiResponse.data.data,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.response ? error.response.data.Message : error.message,
    };
  }
};

// All enquiries (SuperAdmin only — enforced server-side). Returns an array or null on error.
// Drives the internal admin enquiries inbox.
export const getEnquiries = async (token) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: SEND_ENQUIRY_API,
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error getting enquiries', error.message);
    return null;
  }
};

// Move an enquiry through the admin inbox workflow (SuperAdmin only — enforced server-side).
// `status` is one of 'New' | 'Handled' | 'Archived'. Returns { success, reason }.
export const updateEnquiryStatus = async (token, id, status) => {
  try {
    const apiResponse = await axios({
      method: 'PUT',
      baseURL: AUTH_BASE,
      url: `${SEND_ENQUIRY_API}/${id}/status`,
      headers: AUTH_HEADER(token),
      data: { status },
    });

    return {
      success: apiResponse.data.succeeded !== false,
      reason: apiResponse.data.message,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.response ? error.response.data.Message : error.message,
    };
  }
};

// The caller's own API keys (masked, no secrets). Returns an array or null on error.
export const getApiKeys = async (token) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: AUTH_BASE,
      url: API_KEYS_MY,
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data.data;
  } catch (error) {
    console.log('Error getting API keys', error.message);
    return null;
  }
};

// Creates a key. On success returns { success: true, data: { id, name, key, secret } }
// — the secret is only ever returned here, once.
export const createApiKey = async (token, name) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: CREATE_API_KEY,
      headers: AUTH_HEADER(token),
      data: { name },
    });

    return { success: true, data: apiResponse.data.data };
  } catch (error) {
    return {
      success: false,
      reason:
        (error.response && error.response.data && error.response.data.message) ||
        error.message,
    };
  }
};

// Revokes one of the caller's keys. Returns { success, reason? }.
export const revokeApiKey = async (token, id) => {
  try {
    await axios({
      method: 'POST',
      baseURL: AUTH_BASE,
      url: REVOKE_API_KEY(id),
      headers: AUTH_HEADER(token),
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      reason:
        (error.response && error.response.data && error.response.data.message) ||
        error.message,
    };
  }
};

export const createClientSubscription = async (clientObj, token) => {
  try {
    const data = {
      clientId: clientObj.clientId,
      paddlePlanType: clientObj.paddlePlanType,
      paddleCustomerId: clientObj.paddleCustomerId,
      paddleSubscriptionId: clientObj.paddleSubscriptionId,
      paddlePriceId: clientObj.paddlePriceId,
      startDate: clientObj.startDate,
      endDate: clientObj.endDate,
    };

    const res = await axios.post(
      AUTH_BASE + CREATE_CLIENT_SUBSCRIPTION_API,
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            data: data.data,
            request: CREATE_CLIENT_SUBSCRIPTION,
          }
        : {
            success: false,
            reason: data.Message,
            request: CREATE_CLIENT_SUBSCRIPTION,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: CREATE_CLIENT_SUBSCRIPTION,
    };
  }
};

export const updateClientSubscription = async (clientObj, token) => {
  try {
    const data = {
      clientId: clientObj.clientId ? clientObj.clientId : '',
      paddlePlanType: clientObj.paddlePlanType ? clientObj.paddlePlanType : '',
      paddleCustomerId: clientObj.paddleCustomerId
        ? clientObj.paddleCustomerId
        : '',
      paddleSubscriptionId: clientObj.paddleSubscriptionId
        ? clientObj.paddleSubscriptionId
        : '',
      paddlePriceId: clientObj.paddlePriceId ? clientObj.paddlePriceId : '',
      planId: clientObj.planId ? clientObj.planId : '',
      startDate: clientObj.startDate ? clientObj.startDate : '',
      endDate: clientObj.endDate ? clientObj.endDate : '',
      userId: clientObj.userId ? clientObj.userId : '',
    };

    const res = await axios.put(
      AUTH_BASE + UPDATE_CLIENT_SUBSCRIPTION_API(clientObj.clientId),
      JSON.stringify(data),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
            data: data.data,
            request: SUBSCRIPTION_UPDATE,
          }
        : {
            success: false,
            reason: data.Message,
            request: SUBSCRIPTION_UPDATE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: SUBSCRIPTION_UPDATE,
    };
  }
};

export const getUserPlans = async (token) => {
  try {
    const res = await axios.get(AUTH_BASE + GET_PLANS, {
      headers: AUTH_HEADER(token),
    });

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_USER_PLANS,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // Must never throw: this runs inside the login/refresh reader path (see
    // store/User/userActions.logIn). getErrReason is null-safe on a network-level
    // failure, so a transient blip can't blow up login and drop entitlements.
    return {
      success: false,
      reason: getErrReason(e),
      request: GET_USER_PLANS,
    };
  }
};

export const getUserUsage = async (token, userId) => {
  try {
    const res = await axios.get(AUTH_BASE + USAGE_BY_USER_ID_API(userId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_USER_USAGE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: GET_USER_USAGE,
    };
  }
};

export const updateUserUsage = async (obj, token) => {
  try {
    // const data = {
    //   userId: obj.userId,
    //   clientId: obj.clientId,
    //   pageViewUsed: obj.pageViewUsed ? obj.pageViewUsed : 0,
    //   queryUsed: obj.queryUsed ? obj.queryUsed : 0,
    //   summaryUsed: obj.summaryUsed ? obj.summaryUsed : 0,
    //   shareUsed: obj.shareUsed ? obj.shareUsed : 0,
    //   additionalQueryUsed: obj.additionalQueryUsed
    //     ? obj.additionalQueryUsed
    //     : 0,
    // };

    const res = await axios.post(
      AUTH_BASE + UPDATE_USER_USAGE_API,
      JSON.stringify(obj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('res', res);

    if (res && res.data) {
      const { data } = res;
      return res.status === 200
        ? {
            success: true,
            data: data.data,
            request: UPDATE_USER_USAGE,
          }
        : {
            success: false,
            reason: data.Message,
            request: UPDATE_USER_USAGE,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: UPDATE_USER_USAGE,
    };
  }
};

export const createNewQuery = async (token, query) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_NEW_QUERY_API,
      JSON.stringify(query),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('createNewQuery', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      getErrReason(e)
    );
    return {
      success: false,
      reason:
        getErrReason(e),
    };
  }
};

export const updateQuery = async (token, query) => {
  try {
    const res = await axios.put(
      AUTH_BASE + UPDATE_QUERY_API(query.id),
      JSON.stringify(query),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('updateQuery', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      getErrReason(e)
    );
    return {
      success: false,
      reason:
        getErrReason(e),
    };
  }
};

export const deleteQuery = async (token, queryId) => {
  try {
    const res = await axios.delete(AUTH_BASE + DELETE_QUERY_API(queryId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('query', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: true,
          }
        : {
            success: false,
            reason: data.message,
            request: DELETE_BOOKMARK,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    return {
      success: false,
      reason:
        getErrReason(e),
      request: DELETE_BOOKMARK,
    };
  }
};

export const getUserQueries = async (token, userId) => {
  try {
    const res = await axios.get(AUTH_BASE + GET_QUERY_BY_USER_ID_API(userId), {
      headers: AUTH_HEADER(token),
    });

    // console.log('getUserQueries', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_QUERIES,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: GET_USER_PLANS,
    };
  }
};

export const getQueryMessages = async (token, queryId) => {
  try {
    const res = await axios.get(
      AUTH_BASE + GET_QUERY_MESSAGES_BY_QUERY_ID_API(queryId),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('getQueryMessages', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? // success: true,
          data.data
        : // request: GET_USER_PLANS,

          {
            success: false,
            reason: data.Message,
            request: GET_MESSAGES,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: GET_MESSAGES,
    };
  }
};

export const createNewMessage = async (token, message) => {
  try {
    const res = await axios.post(
      AUTH_BASE + CREATE_NEW_MESSAGE_API,
      JSON.stringify(message),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('createNewMessage', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.data,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      getErrReason(e)
    );
    return {
      success: false,
      reason:
        getErrReason(e),
    };
  }
};

export const updateMessageResponse = async (token, messageId, response) => {
  try {
    const res = await axios.put(
      AUTH_BASE + UPDATE_MESSAGES_RESPONSE_API(messageId),
      JSON.stringify(response),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('updateMessageResponse', res);

    if (res && res.data) {
      const { data } = res;
      return data.succeeded === true
        ? {
            success: data.succeeded,
          }
        : {
            success: false,
            reason: data.message,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    console.log(
      'Could not save comment',
      getErrReason(e)
    );
    return {
      success: false,
      reason:
        getErrReason(e),
    };
  }
};

export const createAdditionalPurchase = async (obj, token) => {
  try {
    const res = await axios.post(
      AUTH_BASE + BUY_ADDITIONAL_QUERIES_API,
      JSON.stringify(obj),
      {
        headers: AUTH_HEADER(token),
      }
    );

    // console.log('createAdditionalPurchase', res);

    if (res && res.data) {
      const { data } = res;
      return res.status === 200
        ? {
            success: true,
            data: data.data,
            request: BUY_ADDITIONAL_QUERIES,
          }
        : {
            success: false,
            reason: data.Message,
            request: BUY_ADDITIONAL_QUERIES,
          };
    } else throw new Error(INTERNAL_ERROR);
  } catch (e) {
    // console.log('e', e.response);
    return {
      success: false,
      reason:
        getErrReason(e),
      request: BUY_ADDITIONAL_QUERIES,
    };
  }
};

//#endregion
